import type { ReactNode } from 'react';
import { useState } from 'react';
import { createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EgptAvatarVariant } from 'src/components/ui/egpt-avatar-picker/constants';
import {
  buildProjectChatPath,
  NEW_CHAT_ROUTE
} from 'src/content/ChatV2/constants';
import type { ProjectsContextType } from 'src/content/Projects/types/index';
import type { IBreadcrumbItem } from 'src/types/custom_component';

type ProjectsProviderProps = {
  children: ReactNode;
};

const ProjectsContext = createContext<ProjectsContextType | null>(null);

export const ProjectsProvider = ({ children }: ProjectsProviderProps) => {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const refreshGrid = () => setRefreshKey((k) => k + 1);

  const onBreadcrumbItemClick = (item: IBreadcrumbItem) => {
    if (item.webUrl) {
      navigate(item.webUrl);
    }
  };

  const handleStartNewChat = (
    projectId: number,
    name: string,
    icon?: EgptAvatarVariant
  ) => {
    navigate(buildProjectChatPath(projectId, NEW_CHAT_ROUTE), {
      state: {
        projectChatName: name || '',
        icon
      }
    });
  };

  return (
    <ProjectsContext.Provider
      value={{
        refreshKey,
        isModalOpen,
        openModal,
        closeModal,
        refreshGrid,
        onBreadcrumbItemClick,
        handleStartNewChat
      }}
    >
      {children}
    </ProjectsContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjects must be used within useProjectsProvider');
  }
  return context;
};
